import ActivityTracker, { WorkoutConfirmationData, WorkoutType } from '../plugins/ActivityTrackerPlugin'
import { ActivityEntry } from './api.types'
import { activityUploadService, UploadEvent, UploadStats } from './ActivityUploadService'
import { store } from '../index'
import { push } from 'connected-react-router'
import { ROUTES } from '../common/Routes/routePaths'
import { IGlobalState } from '../Root/store/appReducer'
import { getUserCompanies } from '../common/auth/authSelectors'
import { hasFeatureFlag } from '../common/helpers/featureFlags'

/**
 * Global service for handling activity tracker events across the entire app
 * This ensures workout completion events are handled regardless of which view is active
 */
class GlobalActivityTrackerService {
  private static instance: GlobalActivityTrackerService
  private isInitialized = false
  private listeners: {
    workoutCompleted?: () => void
    confirmationRequired?: () => void
    uploadEvent?: () => void
  } = {}

  // Global state for workout tracking
  private globalState = {
    isVisible: false,
    isLoading: false,
    error: null as string | null,
    successMessage: null as string | null,
    confirmationData: null as WorkoutConfirmationData | null,
    isConfirmationRequired: false,
    uploadStats: {
      queuedCount: 0,
      isOnline: true,
      isProcessing: false,
    } as UploadStats,
  }

  // Callbacks for UI updates
  private stateUpdateCallbacks: Array<(state: typeof this.globalState) => void> = []

  static getInstance(): GlobalActivityTrackerService {
    if (!GlobalActivityTrackerService.instance) {
      GlobalActivityTrackerService.instance = new GlobalActivityTrackerService()
    }
    return GlobalActivityTrackerService.instance
  }

  /**
   * Initialize the global service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('🔧 GlobalActivityTrackerService: Already initialized')
      return
    }

    try {
      console.log('🔧 GlobalActivityTrackerService: Initializing global service')

      // Initialize upload service
      await activityUploadService.initialize()

      // Set up global event listeners
      await this.setupGlobalListeners()

      // Check for active workout on app start
      await this.checkForActiveWorkout()

      this.isInitialized = true
      console.log('✅ GlobalActivityTrackerService: Initialized successfully')
    } catch (error) {
      console.error('❌ GlobalActivityTrackerService: Failed to initialize:', error)
    }
  }

  /**
   * Set up global event listeners for workout events
   */
  private async setupGlobalListeners(): Promise<void> {
    try {
      console.log('🔧 GlobalActivityTrackerService: Setting up global event listeners')

      // Set up workout completion listener
      const workoutListener = await ActivityTracker.addListener('workoutCompleted', (data) => {
        console.log('🔧 GlobalActivityTrackerService: workoutCompleted event received!', data)
        this.handleWorkoutCompleted(data.activityData, data.routeData)
      })
      this.listeners.workoutCompleted = workoutListener.remove

      // Set up workout confirmation required listener
      const confirmationListener = await ActivityTracker.addListener('workoutConfirmationRequired', (data) => {
        console.log('🔧 GlobalActivityTrackerService: workoutConfirmationRequired event received!', data)
        this.handleWorkoutConfirmationRequired(data.confirmationData)
      })
      this.listeners.confirmationRequired = confirmationListener.remove

      // Set up upload service event listener
      const uploadListener = activityUploadService.addListener((event: UploadEvent) => {
        console.log('🔧 GlobalActivityTrackerService: Upload event received:', event)
        this.handleUploadEvent(event)
      })
      this.listeners.uploadEvent = uploadListener

      console.log('✅ GlobalActivityTrackerService: Global event listeners set up')
    } catch (error) {
      console.error('❌ GlobalActivityTrackerService: Failed to set up listeners:', error)
    }
  }

  /**
   * Check for active workout on app start
   */
  private async checkForActiveWorkout(): Promise<void> {
    try {
      console.log('🔍 GlobalActivityTrackerService: Checking for active workout on app start')
      const isVisible = await ActivityTracker.isVisible()

      if (isVisible.visible) {
        console.log('✅ GlobalActivityTrackerService: Found active workout - setting global state')
        this.updateGlobalState({ isVisible: true })

        // Send default metric labels when workout is restored
        await this.sendDefaultMetricLabels()
      } else {
        console.log('ℹ️ GlobalActivityTrackerService: No active workout found')
        this.updateGlobalState({ isVisible: false })
      }
    } catch (err) {
      console.error('❌ GlobalActivityTrackerService: Failed to check for active workout:', err)
      this.updateGlobalState({ isVisible: false })
    }
  }

  /**
   * Send default metric labels to Swift plugin
   */
  private async sendDefaultMetricLabels(): Promise<void> {
    try {
      console.log('🔧 GlobalActivityTrackerService: Sending default metric labels for restored workout')

      // Create default labels mapping
      const labelsMapping: Record<string, string> = {
        // Metric labels
        time: 'Time',
        distance: 'Distance',
        speed: 'Speed',
        pace: 'Avg Pace',
        steps: 'Steps',
        calories: 'Calories',
        // Units - time should have no unit
        time_unit: '',
        distance_unit: 'km',
        speed_unit: 'km/h',
        pace_unit: 'min/km',
        steps_unit: 'total',
        calories_unit: 'kcal',
        // Default to metric
        user_units: 'METRIC',
      }

      // Send labels to Swift plugin
      await ActivityTracker.setActivityLabels({ labels: labelsMapping })
      console.log('✅ GlobalActivityTrackerService: Sent default metric labels to Swift plugin for restored workout')
    } catch (error) {
      console.error('❌ GlobalActivityTrackerService: Failed to send default metric labels:', error)
    }
  }

  /** True if the user's company allows location tracking (reads the Redux store directly). */
  private isLocationTrackingEnabled(): boolean {
    try {
      const state = store.getState() as unknown as IGlobalState
      return hasFeatureFlag(getUserCompanies(state), 'locationTracking')
    } catch {
      return false
    }
  }

  /**
   * Handle workout completion
   */
  private async handleWorkoutCompleted(activityData: ActivityEntry, routeData?: any[]): Promise<void> {
    console.log('🔧 GlobalActivityTrackerService: Handling workout completion', activityData)

    this.updateGlobalState({ isLoading: true, error: null, successMessage: null })

    // Defense-in-depth: drop any route data if the company has location tracking disabled. Native
    // already skips emitting routes in that case, but never upload location data regardless.
    const route = this.isLocationTrackingEnabled() ? routeData : undefined

    try {
      const result = await activityUploadService.uploadActivity(activityData, route)

      if (result.success) {
        console.log('✅ GlobalActivityTrackerService: Activity uploaded successfully')
        this.updateGlobalState({
          successMessage: 'Activity recorded successfully!',
          isVisible: false,
          isLoading: false,
        })
      } else if (result.wasQueued) {
        console.log('📦 GlobalActivityTrackerService: Activity queued for offline upload')
        this.updateGlobalState({
          successMessage: 'Activity saved - will upload when online!',
          isVisible: false,
          isLoading: false,
        })
      } else {
        console.log('❌ GlobalActivityTrackerService: Upload failed without queuing')
        this.updateGlobalState({
          error: result.error || 'Failed to upload activity data',
          isLoading: false,
        })
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload activity'
      console.error('❌ GlobalActivityTrackerService: Failed to upload activity:', err)
      this.updateGlobalState({
        error: errorMessage,
        isLoading: false,
      })
    }
  }

  /**
   * Handle workout confirmation required
   */
  private handleWorkoutConfirmationRequired(data: WorkoutConfirmationData): void {
    console.log('🤔 GlobalActivityTrackerService: Workout confirmation required:', data)
    console.log('🤔 GlobalActivityTrackerService: Confirmation data details:', {
      title: data.title,
      type: data.type,
      duration: data.duration,
      distance: data.distance,
      calories: data.calories,
      steps: data.steps,
      locationsCount: data.locations?.length || 0,
    })

    this.updateGlobalState({
      confirmationData: {
        ...data,
        startTime: data.startTime,
        endTime: data.endTime,
      },
      isConfirmationRequired: true,
      isVisible: false,
    })
    console.log('✅ GlobalActivityTrackerService: Confirmation state set - isConfirmationRequired: true')
  }

  /**
   * Handle upload service events
   */
  private handleUploadEvent(event: UploadEvent): void {
    console.log('📡 GlobalActivityTrackerService: Upload event:', event)

    switch (event.type) {
      case 'upload_success':
        if (!event.data?.wasRetry) {
          this.updateGlobalState({ successMessage: 'Activity uploaded successfully!' })
        }
        break
      case 'upload_failed':
        if (!event.data?.queueId) {
          this.updateGlobalState({ error: event.data?.error || 'Upload failed' })
        }
        break
      case 'queue_added':
        this.updateGlobalState({ successMessage: 'Activity saved for offline upload!' })
        break
      case 'network_changed':
        this.updateGlobalState({ uploadStats: activityUploadService.getStats() })
        break
    }
  }

  /**
   * Update global state and notify callbacks
   */
  private updateGlobalState(updates: Partial<typeof this.globalState>): void {
    this.globalState = { ...this.globalState, ...updates }
    this.stateUpdateCallbacks.forEach((callback) => callback(this.globalState))
  }

  /**
   * Subscribe to state updates
   */
  subscribe(callback: (state: typeof this.globalState) => void): () => void {
    this.stateUpdateCallbacks.push(callback)
    // Return unsubscribe function
    return () => {
      const index = this.stateUpdateCallbacks.indexOf(callback)
      if (index > -1) {
        this.stateUpdateCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Get current global state
   */
  getState(): typeof this.globalState {
    return { ...this.globalState }
  }

  /**
   * Confirm a workout with the provided title and type
   */
  async confirmWorkout(title: string, type: WorkoutType): Promise<void> {
    try {
      console.log('✅ GlobalActivityTrackerService: Confirming workout with:', { title, type })

      const result = await ActivityTracker.confirmWorkout({ title, type })

      if (result.success) {
        console.log('✅ GlobalActivityTrackerService: Workout confirmed successfully')
        this.updateGlobalState({
          isConfirmationRequired: false,
          confirmationData: null,
          isVisible: false,
        })

        // Navigate to activities view after successful confirmation
        console.log('🧭 GlobalActivityTrackerService: Navigating to activities view')
        // Add a small delay to ensure state is properly updated before navigation
        setTimeout(() => {
          store.dispatch(push(ROUTES.ACTIVITIES.to))
        }, 100)
      } else {
        throw new Error('Failed to confirm workout')
      }
    } catch (error) {
      console.error('❌ GlobalActivityTrackerService: Failed to confirm workout:', error)
      this.updateGlobalState({
        error: error instanceof Error ? error.message : 'Failed to confirm workout',
      })
      throw error
    }
  }

  /**
   * Discard a workout
   */
  async discardWorkout(): Promise<void> {
    try {
      console.log('🗑️ GlobalActivityTrackerService: Discarding workout')

      const result = await ActivityTracker.discardWorkout()

      if (result.success) {
        console.log('✅ GlobalActivityTrackerService: Workout discarded successfully')
        this.updateGlobalState({
          isConfirmationRequired: false,
          confirmationData: null,
          isVisible: false,
        })
      } else {
        throw new Error('Failed to discard workout')
      }
    } catch (error) {
      console.error('❌ GlobalActivityTrackerService: Failed to discard workout:', error)
      this.updateGlobalState({
        error: error instanceof Error ? error.message : 'Failed to discard workout',
      })
      throw error
    }
  }

  /**
   * Clean up listeners
   */
  cleanup(): void {
    console.log('🧹 GlobalActivityTrackerService: Cleaning up global listeners')

    if (this.listeners.workoutCompleted) {
      this.listeners.workoutCompleted()
    }
    if (this.listeners.confirmationRequired) {
      this.listeners.confirmationRequired()
    }
    if (this.listeners.uploadEvent) {
      this.listeners.uploadEvent()
    }

    this.listeners = {}
    this.stateUpdateCallbacks = []
    this.isInitialized = false
  }
}

// Export singleton instance
export const globalActivityTrackerService = GlobalActivityTrackerService.getInstance()
